import * as React from "react";
import { PlusIcon, XIcon } from "../icons";
import { FieldLabel } from "./FormSection";
import { Button } from "../ui/button";
import { Tooltip } from "../ui/tooltip";

export interface EditorCardProps {
  /** Names the row -- "Condition 2", "Column 1". */
  title: React.ReactNode;
  onRemove: () => void;
  /** Accessible name for the remove control; the tooltip shows it too. */
  removeLabel: string;
  children: React.ReactNode;
}

/**
 * One repeatable row of a list the user is building up: a condition, a template column.
 *
 * The two editors that need this drew it differently -- one titled the card with a
 * caption, the other hid the name inside a floating TextField label -- and both hung a
 * permanently red delete icon off the corner. Red is for the outcome of pressing a
 * control, not for its resting state, so this one is quiet until it is hovered.
 */
const EditorCard = ({ title, onRemove, removeLabel, children }: EditorCardProps) => (
  <div className="rounded-lg border border-border bg-grey-50 p-3">
    <div className="mb-2.5 flex flex-row items-center justify-between">
      <FieldLabel component="span">{title}</FieldLabel>
      <Tooltip title={removeLabel}>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={removeLabel}
          onClick={onRemove}
          className="-mr-1 text-muted-foreground hover:bg-transparent hover:text-error"
        >
          <XIcon className="size-4" />
        </Button>
      </Tooltip>
    </div>
    <div className="flex flex-col gap-2.5">{children}</div>
  </div>
);

export default EditorCard;

/**
 * The control that appends another {@link EditorCard}. Dashed and full width so it reads
 * as the empty slot at the end of the list rather than as the section's primary action --
 * the one real button on these screens is Insert reference, at the foot of the form.
 */
export const AddRowButton = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Button
    variant="secondary-outline"
    onClick={onClick}
    className="w-full border-dashed border-grey-300 text-muted-foreground hover:border-grey-400 hover:bg-grey-50"
  >
    <PlusIcon />
    {children}
  </Button>
);
